import { useQuery, type UseQueryResult } from '@tanstack/react-query';

import { chamarApi } from '@/nucleo/auth/cliente-api';
import { naoInsistirEmRecusa } from '@/nucleo/naoInsistirEmRecusa';

import type { MotivoDaPendencia, SituacaoOperacionalDoPortal } from './contrato';

export const CAMINHO_DA_SITUACAO = '/bff/v1/portal-credenciado/situacao';

const FALAR_COM_A_DTP = { rotulo: 'Falar com a DTP', caminho: '/credenciado/solicitacoes/nova' };

/**
 * Título, texto e atalho de cada motivo, os mesmos do protótipo
 * (`usePortalGatingStatus`). O motivo vem do servidor; o texto é da tela.
 */
const TEXTO_DO_MOTIVO: Record<
  Exclude<MotivoDaPendencia, 'OK'>,
  Omit<SituacaoOperacionalDoPortal, 'motivo'>
> = {
  SUSPENSO: {
    severidade: 'destructive',
    titulo: 'Credenciamento suspenso',
    detalhe:
      'Enquanto a suspensão estiver vigente, as ações operacionais ficam bloqueadas. Procure a DTP para regularizar.',
    cta: FALAR_COM_A_DTP,
    bloqueia: true,
  },
  INATIVO: {
    severidade: 'destructive',
    titulo: 'Credenciamento inativo',
    detalhe: 'Seu credenciamento está inativo. Novas operações não são permitidas.',
    bloqueia: true,
  },
  TERMO_NAO_ASSINADO: {
    severidade: 'warning',
    titulo: 'Termo aguardando assinatura',
    detalhe:
      'Seu termo de credenciamento ainda não foi assinado. Assine para liberar as operações.',
    cta: { rotulo: 'Assinar termo agora', caminho: '/credenciado/contratos' },
    bloqueia: true,
  },
  INSCRICAO_EM_ANALISE: {
    severidade: 'info',
    titulo: 'Inscrição em análise',
    detalhe:
      'Sua inscrição está em análise pela DTP. Algumas funcionalidades só ficam disponíveis após a emissão do termo.',
    cta: { rotulo: 'Acompanhar inscrição', caminho: '/credenciado/inscricao' },
    bloqueia: true,
  },
  SEM_TERMO_ATIVO: {
    severidade: 'warning',
    titulo: 'Sem termo vigente',
    detalhe: 'Você não possui termo vigente no momento. Procure a DTP para regularizar.',
    cta: FALAR_COM_A_DTP,
    bloqueia: true,
  },
  CADIN_PENDENTE: {
    severidade: 'warning',
    titulo: 'Pendência no CADIN Municipal',
    detalhe: 'Há pendência ativa no CADIN. Regularize para evitar bloqueio de pagamentos.',
    cta: { rotulo: 'Ver cobranças', caminho: '/credenciado/cobrancas' },
    bloqueia: false,
  },
};

const SEM_PENDENCIA: SituacaoOperacionalDoPortal = {
  motivo: 'OK',
  severidade: 'info',
  titulo: '',
  detalhe: '',
  bloqueia: false,
};

/** Motivo que a tela não conhece não vira faixa sem título: sem pendência. */
export function situacaoDoMotivo(motivo: MotivoDaPendencia): SituacaoOperacionalDoPortal {
  if (motivo === 'OK' || !Object.hasOwn(TEXTO_DO_MOTIVO, motivo)) return SEM_PENDENCIA;
  return { motivo, ...TEXTO_DO_MOTIVO[motivo] };
}

/**
 * A pendência operacional do credenciado logado — a mesma pergunta que
 * `usePortalGatingStatus` respondia no protótipo, decidida pelo servidor
 * (`portal_credenciado_situacao`, migração 0263). Fonte única de
 * `GatingBanner` e de quem desabilita ação (`InscreverTerceiroPage`): duas
 * telas com motivos diferentes para o mesmo credenciado seria pior do que
 * nenhuma.
 *
 * Quem abre o portal sem cadastro vinculado (administração interna,
 * pré-credenciado) recebe OK do servidor, como no protótipo, que só
 * consultava com um credenciado. Falha na consulta não inventa pendência:
 * sem resposta, a faixa não aparece.
 */
export function useSituacaoOperacionalDoPortal(): UseQueryResult<
  SituacaoOperacionalDoPortal,
  Error
> {
  return useQuery<SituacaoOperacionalDoPortal, Error>({
    queryKey: ['credenciado', 'situacao-operacional'] as const,
    queryFn: async () =>
      situacaoDoMotivo(
        (await chamarApi<{ motivo: MotivoDaPendencia }>(CAMINHO_DA_SITUACAO)).motivo,
      ),
    staleTime: 2 * 60_000,
    retry: naoInsistirEmRecusa,
  });
}
